import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import LinearProgress from '@mui/material/LinearProgress';
import { ErrorMessage } from '@hookform/error-message';
import CloseIcon from '@mui/icons-material/Close';
import { useHotkeys } from 'react-hotkeys-hook';
import { useForm } from 'react-hook-form';
import { useDebounce } from 'usehooks-ts';
import { Tooltip } from '@mui/material';

import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { GROUP_WITH_SUBGROUP, USER_NEW } from '../../../enums/TYPES';
import { postCreateNewGroup } from '../../../api/groupsApi';
import { useAppSelector } from '../../../store/hooks';
import SearchInput from '../../modules/SearchInput';
import { NOTIFICATION } from '../../../enums/ENUMS';
import { getUsersByRolesArray } from '../../../api';
import styles from './CreateGroupModal.module.scss';
import { RolesLowerCase } from '../../../enums';
import RolePlate from '../../modules/RolePlate';
import { displayRole } from '../../../helpers';
import Avatar from '../../modules/Avatar';
import Modal from '../../modules/Modal';

type TCreateGroupModal = {
  open: boolean;
  switchModal: () => void;
  groups: GROUP_WITH_SUBGROUP[];
  setFetchTrigger: (num: number) => void;
};

type TFormData = {
  title: string;
  analysts: string[];
  viewers: string[];
};

const CreateGroupModal: React.FC<TCreateGroupModal> = ({ open, switchModal, groups, setFetchTrigger }) => {
  const canViewEmails = useCanViewEmails();
  const [step, setStep] = useState(1);
  const notification = useSnackbar();
  const [progress, setProgress] = useState(30);
  const toSecondStepButtonRef = useRef<HTMLDivElement>(null);
  const toFourthStepButtonRef = useRef<HTMLDivElement>(null);
  const backToFirstStepButtonRef = useRef<HTMLDivElement>(null);
  const backToThirdStepButtonRef = useRef<HTMLDivElement>(null);
  const submitButtonRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TFormData>({
    defaultValues: {
      title: '',
      analysts: [],
      viewers: [],
    },
  });
  const [form, setForm] = useState<TFormData>();
  const [newAnalysts, setNewAnalysts] = useState<string[]>([]);
  const [newViewers, setNewViewers] = useState<string[]>([]);
  const [errMsg, setErrMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAppSelector((state) => state.user);

  const [selectAllOa, setSelectAllOa] = useState(false);
  const [selectAllAnalyst, setSelectAllAnalyst] = useState(false);

  const toOAStep = (data: TFormData) => {
    let exists = false;
    if (groups.length > 0) {
      for (let i = 0; i < groups.length; i++) {
        if (groups[i]?.title.toLowerCase() === data?.title.toLowerCase()) {
          notification.showMessage(NOTIFICATION.GROUP_NAME_ALREADY_EXISTS);
          exists = true;
          break;
        }
      }
    }
    if (exists) {
      return;
    }
    setProgress(50);
    setStep(2);
    setForm(data);
  };

  const toAnalystStep = (data: TFormData) => {
    setProgress(75);
    setForm(data);
    analystList.length ? setStep(3) : toViewersStep();
  };

  const toViewersStep = () => {
    setProgress(100);

    // @ts-ignore
    setForm({ ...form, analysts: newAnalysts });
    viewerstList.length ? setStep(4) : onSubmit();
  };

  const onClose = () => {
    switchModal();
    setNewAnalysts([]);
    setNewViewers([]);
    setStep(1);
    setProgress(30);
    setSelectAllOa(false);
    setSelectAllAnalyst(false);
    setAllDone(false);
    reset();
  };

  const onSelectAll = (e: React.ChangeEvent<HTMLInputElement>, role: RolesLowerCase) => {
    setErrMsg('');
    if (role === RolesLowerCase.ORGANIZATION_ADMIN) {
      const oasIds = OAsList.map((oa) => `${oa.user.id}`);
      if (currentUser?.role.title === role) {
        oasIds.push(`${currentUser.id}`);
      }

      setNewAnalysts(!selectAllOa ? [...newAnalysts, ...oasIds] : [...newAnalysts.filter((oa) => !oasIds.includes(oa))]);
      setSelectAllOa(!selectAllOa);
    } else if (role === RolesLowerCase.ANALYST) {
      const analystsIds = analystList.map((analyst) => `${analyst.user.id}`);

      setNewAnalysts(
        !selectAllAnalyst ? [...newAnalysts, ...analystsIds] : [...newAnalysts.filter((analyst) => !analystsIds.includes(analyst))],
      );
      setSelectAllAnalyst(!selectAllAnalyst);
    } else if (role === RolesLowerCase.VIEWER) {
      setNewViewers(newViewers.length === viewerstList.length ? [] : [...newViewers, ...viewerstList.map((viewer) => `${viewer.user.id}`)]);
    }
  };

  const onAnalystAdd = (e: any) => {
    setErrMsg('');
    if (newAnalysts.includes(e.target.id)) {
      const arr = newAnalysts.filter((str) => str !== e.target.id);
      setNewAnalysts(arr);
    } else {
      setNewAnalysts([...newAnalysts, `${e.target.id}`]);
    }
  };

  const onViewerAdd = (e: any) => {
    setErrMsg('');
    if (newViewers.includes(e.target.id)) {
      const arr = newViewers.filter((str) => str !== e.target.id);
      setNewViewers(arr);
    } else {
      setNewViewers([...newViewers, `${e.target.id}`]);
    }
  };

  const [allDone, setAllDone] = useState(false);
  const onSubmit = async () => {
    if (newAnalysts.length + newViewers.length < 2) {
      setErrMsg('Add at least 2 users');
      return;
    }
    setLoading(true);
    try {
      // @ts-ignore
      await postCreateNewGroup({
        ...form,
        analysts: newAnalysts.map((n) => Number(n)),
        viewers: newViewers.map((n) => Number(n)),
      });
      setFetchTrigger(Date.now());
      setAllDone(true);
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage(NOTIFICATION.SOMETHING_WENT_WRONG);
      }
    } finally {
      setLoading(false);
    }
  };

  const [OAsList, setOAsList] = useState<USER_NEW[]>([]);
  const [analystList, setAnalystList] = useState<USER_NEW[]>([]);
  const [viewerstList, setViewersList] = useState<USER_NEW[]>([]);
  const [searchAnalyst, setSearchAnalyst] = useState('');
  const debouncedValueAnalyst = useDebounce<string>(searchAnalyst, 500);
  const [searchViewer, setSearchViewer] = useState('');
  const debouncedValueViewer = useDebounce<string>(searchViewer, 500);

  useEffect(() => {
    getUsersByRolesArray([RolesLowerCase.ORGANIZATION_ADMIN], debouncedValueAnalyst, [currentUser!.id]).then((res) =>
      setOAsList(res.documents),
    );
    getUsersByRolesArray([RolesLowerCase.ANALYST], debouncedValueAnalyst, [currentUser!.id]).then((res) => setAnalystList(res.documents));
    getUsersByRolesArray([RolesLowerCase.VIEWER], debouncedValueViewer).then((res) => setViewersList(res.documents));
  }, [step, debouncedValueAnalyst, debouncedValueViewer]);

  const onSearchAnalyst = (e: React.ChangeEvent<HTMLInputElement>) => setSearchAnalyst(e.target.value);
  const onSearchViewer = (e: React.ChangeEvent<HTMLInputElement>) => setSearchViewer(e.target.value);

  const stepBack = () => {
    setStep(step - 1);
    setProgress(progress - 25);
  };

  useEffect(() => {
    if (currentUser && !OAsList.find((u) => u.user.id === currentUser.id) && !analystList.find((u) => u.user.id === currentUser.id)) {
      const isOa = currentUser.role.title === RolesLowerCase.ORGANIZATION_ADMIN;
      const arr = isOa ? [...OAsList] : [...analystList];
      // @ts-ignore
      arr.push({ ...currentUser, user: currentUser });
      isOa ? setOAsList(arr) : setAnalystList(arr);
    }
  }, [currentUser, OAsList, analystList]);

  const analystUserChips = useMemo(() => {
    const listOfChips =
      step === 2
        ? OAsList.filter((u) => newAnalysts.includes(`${u.user.id}`))
        : analystList.filter((u) => newAnalysts.includes(`${u.user.id}`));
    return listOfChips?.map((u) => (
      <div key={`chip_${u.user.id}`} className={styles.chip}>
        <span>{u?.user.username ? u.user.username : (canViewEmails ? u.user.email : 'User')}</span>{' '}
        <CloseIcon
          onClick={() => onAnalystAdd({ target: { id: u.user.id } })}
          className={styles.removeChip}
          sx={{ color: 'black', fontSize: '14px', marginLeft: '8px' }}
        />
      </div>
    ));
  }, [OAsList, newAnalysts, step]);

  const viewerChips = useMemo(() => {
    const listOfChips = viewerstList.filter((u) => newViewers.includes(`${u.user.id}`));
    return listOfChips?.map((u) => (
      <div key={`chip_${u.id}`} className={styles.chip}>
        <span>{u?.user.username ? u.user.username : (canViewEmails ? u.user.email : 'User')}</span>{' '}
        <CloseIcon
          onClick={() => onViewerAdd({ target: { id: u.user.id } })}
          className={styles.removeChip}
          sx={{ color: 'black', fontSize: '14px', marginLeft: '8px' }}
        />
      </div>
    ));
  }, [viewerstList, newViewers, step]);

  useHotkeys('Enter', () => {
    if (!open || loading || allDone) return;

    switch (step) {
      case 1:
        toSecondStepButtonRef?.current?.click();
        break;
      case 2:
      case 3:
        toFourthStepButtonRef?.current?.click();
        break;
      case 4:
        submitButtonRef?.current?.click();
        break;
      default:
        break;
    }
  });

  useHotkeys('Backspace', () => {
    if (!open || loading || allDone) return;

    switch (step) {
      case 2:
      case 3:
        backToFirstStepButtonRef?.current?.click();
        break;
      case 4:
        backToThirdStepButtonRef?.current?.click();
        break;
      default:
        break;
    }
  });

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      {allDone ? (
        <div className={styles.successModal}>
          <div className={styles.title}>You have successfully created new Group 🎉</div>
          <ContainedButton onClick={onClose}>Close</ContainedButton>
        </div>
      ) : (
        <div className={styles.modalBody}>
          <div className={styles.modalTitle}>
            {step === 1 && <div className={styles.title}>Create a group</div>}
            {step === 2 && <div className={styles.title}>Add Org. Admins list</div>}
            {step === 3 && <div className={styles.title}>Add Analysts list</div>}
            {step === 4 && <div className={styles.title}>Add Viewers list</div>}
            <div className={styles.step}>Step {step}/4</div>
          </div>
          <LinearProgress className={styles.progressBar} variant="determinate" value={progress} />

          {step === 1 && (
            <form onSubmit={handleSubmit(toOAStep)}>
              <input
                {...register('title', {
                  required: 'Please enter a group name',
                })}
                className={styles.field}
                placeholder="Group Name"
                type="text"
                style={{ border: errors?.title && '1px solid #FF2323' }}
              />
              <ErrorMessage errors={errors} name="title" render={({ message }) => <div className={styles.errMsg}>{message}</div>} />
              <div className={styles.actionBtns}>
                <ContainedButton type="submit">
                  <div ref={toSecondStepButtonRef}>Next</div>
                </ContainedButton>
                <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
              </div>
            </form>
          )}

          {(step === 2 || step === 3) && (
            <form style={{ position: 'relative' }} onSubmit={handleSubmit(step === 2 ? toAnalystStep : toViewersStep)}>
              <SearchInput className={styles.searchInput} placeHolder="Search by email/name" onChange={onSearchAnalyst} />
              <div className={styles.userChips}>{analystUserChips}</div>
              <div className={styles.usersList}>
                <label htmlFor={step === 2 ? 'selectAllOa' : 'selectAllAnalyst'}>
                  <div className={styles.userLine}>
                    <div className={styles.userInfo}>
                      <div className={styles.info} style={{ marginLeft: '0px' }}>
                        <div className={styles.name}>Select All</div>
                      </div>
                    </div>
                    <input
                      id={step === 2 ? 'selectAllOa' : 'selectAllAnalyst'}
                      onChange={(e) => onSelectAll(e, step === 2 ? RolesLowerCase.ORGANIZATION_ADMIN : RolesLowerCase.ANALYST)}
                      type="checkbox"
                      checked={step === 2 ? selectAllOa : selectAllAnalyst}
                    />
                  </div>
                </label>
                {(step === 2 ? OAsList : analystList).map((analyst, i) => (
                  <label htmlFor={analyst.user.id} key={`${analyst.user.id}_${i}`}>
                    <div className={styles.userLine}>
                      <div className={styles.userInfo}>
                        <Avatar avatar={analyst.user.avatar_url} />
                        <div className={styles.info}>
                          <Tooltip title={analyst?.user.username}>
                            <div className={styles.name}>{analyst.user.username}</div>
                          </Tooltip>
                          {canViewEmails && (<Tooltip title={analyst?.user.email}>
                            <div className={styles.email}>{analyst.user.email}</div>
                          </Tooltip>)}
                        </div>
                      </div>
                      <div className={styles.horizontalContainer}>
                        <RolePlate userRole={displayRole(analyst.role.title)} />
                        <input
                          onChange={onAnalystAdd}
                          id={`${analyst.user.id}`}
                          type="checkbox"
                          checked={newAnalysts.includes(`${analyst.user.id}`)}
                        />
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <span className={styles.errMsg}>{errMsg}</span>
              <div className={styles.actionBtns}>
                <ContainedButton type="submit">
                  <div ref={toFourthStepButtonRef}>Next</div>
                </ContainedButton>
                <OutlinedButton kind="default" onClick={stepBack}>
                  <div ref={backToFirstStepButtonRef}>Back</div>
                </OutlinedButton>
              </div>
              <div className={styles.shadowHelper} />
            </form>
          )}

          {step === 4 && (
            <form onSubmit={handleSubmit(onSubmit)} style={{ position: 'relative' }}>
              <SearchInput className={styles.searchInput} placeHolder="Search by email/name" onChange={onSearchViewer} />
              <div className={styles.userChips}>{viewerChips}</div>
              <div className={styles.usersList}>
                <label htmlFor="selectAllViewers">
                  <div className={styles.userLine}>
                    <div className={styles.userInfo}>
                      <div className={styles.info} style={{ marginLeft: '0px' }}>
                        <div className={styles.name}>Select All</div>
                      </div>
                    </div>
                    <div className={'checkbox-wrapper-42'}>
                      <input
                        id="selectAllViewers"
                        onChange={(e) => onSelectAll(e, RolesLowerCase.VIEWER)}
                        type="checkbox"
                        defaultChecked={false}
                      />
                    </div>
                  </div>
                </label>
                {viewerstList.map((viewer, i) => (
                  <label htmlFor={viewer.user.id} key={`${viewer.user.id}_${i}`}>
                    <div className={styles.userLine}>
                      <div className={styles.userInfo}>
                        <Avatar avatar={viewer.user.avatar_url} />
                        <div className={styles.info}>
                          <Tooltip title={viewer?.user.username}>
                            <div className={styles.name}>{viewer.user.username}</div>
                          </Tooltip>
                          {canViewEmails && (<Tooltip title={viewer?.user.email}>
                            <div className={styles.email}>{viewer.user.email}</div>
                          </Tooltip>)}
                        </div>
                      </div>
                      <div className={styles.horizontalContainer}>
                        <RolePlate userRole={displayRole(viewer.role.title)} />
                        <input
                          onChange={onViewerAdd}
                          id={`${viewer.user.id}`}
                          type="checkbox"
                          checked={newViewers.includes(`${viewer.user.id}`)}
                        />
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {viewerstList.length === 0 && (
                <span className={styles.errMsg}>
                  Unable to create a group (no viewers in organisation) <br />
                </span>
              )}
              <span className={styles.errMsg}>{errMsg}</span>
              <div className={styles.actionBtns}>
                <ContainedButton type="submit">
                  <div ref={submitButtonRef}>Create</div>
                </ContainedButton>
                <OutlinedButton kind="default" onClick={stepBack}>
                  <div ref={backToThirdStepButtonRef}>Back</div>
                </OutlinedButton>
              </div>
              <div className={styles.shadowHelper} />
            </form>
          )}
        </div>
      )}
    </Modal>
  );
};

export default CreateGroupModal;
